import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import usePageMeta from '../utils/usePageMeta';

const UPDATED = 'October 30, 2026';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-display text-2xl font-bold text-ds-text">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-text">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  usePageMeta({
    title: 'Privacy Policy',
    description: 'How Doritostats collects, uses, stores, and deletes your data.',
    canonical: '/privacy',
  });

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <Header />
      <div className="bg-forest border-b-4 border-mustard">
        <div className="mx-auto max-w-4xl px-6 py-8">
          <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">Legal</p>
          <h1 className="font-display text-4xl font-black leading-none text-white">Privacy Policy</h1>
          <p className="mt-2 text-sm font-semibold text-white/60">Last updated {UPDATED}</p>
        </div>
      </div>

      <main className="mx-auto w-full max-w-4xl flex-1 space-y-10 px-6 py-10">
        <p className="text-sm leading-relaxed text-text">
          Doritostats is a free fantasy football analytics site run by one person. This page explains
          what data the site collects, why, how long it is kept, and how to delete it. If anything here
          is unclear, use the <Link to="/contact/" className="text-green font-semibold hover:text-mustard">contact page</Link>.
        </p>

        <Section title="What we collect">
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>Account details.</strong> Your email address and a hashed password.</li>
            <li>
              <strong>League connection details.</strong> For ESPN private leagues, the SWID and espn_s2
              cookies you provide. For Sleeper, your Sleeper username. For Yahoo, OAuth tokens issued by
              Yahoo when you connect your account. Yahoo tokens are encrypted at rest. We never see your
              Yahoo password.
            </li>
            <li>
              <strong>League data.</strong> League settings, team and manager names, matchups, scores,
              rosters, and transactions fetched from your fantasy platform so the site can show you
              analytics.
            </li>
            <li>
              <strong>Things you type in.</strong> Owner aliases, rivalry names, League Cup settings,
              commissioner notes, and an optional Discord webhook URL.
            </li>
            <li>
              <strong>Usage data.</strong> Page views and basic interaction events through Google Analytics
              and Microsoft Clarity (see Third parties below).
            </li>
          </ul>
        </Section>

        <Section title="How we use it">
          <p>
            Only to run the site: fetch your leagues, compute awards, standings, luck index, power
            rankings, simulations, records, and send optional Discord recaps you configure. We do not sell
            data, show ads, or use your data for advertising or profiling.
          </p>
        </Section>

        <Section title="How long we keep it">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>ESPN and Sleeper leagues.</strong> Weekly results are kept in a historical store so
              League History, records, and leaderboards work across seasons, until you remove the league
              or delete your account.
            </li>
            <li>
              <strong>Yahoo leagues.</strong> Weekly stats are never written to a permanent store. They are
              held in a short-lived cache (at most 24 hours for box scores, 7 days for projections) and
              re-fetched from Yahoo when needed. We keep only the league link itself, trades, owner
              aliases and rivalry names you create, and results we compute ourselves (simulations, League
              Cup). AI features are not available for Yahoo leagues and Yahoo data is never sent to an AI
              service.
            </li>
            <li>
              <strong>Cached pages.</strong> Most computed pages are cached for 10 minutes.
            </li>
          </ul>
        </Section>

        <Section title="Deleting your data">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Remove a league</strong> from the dashboard. For Yahoo leagues this permanently
              deletes the league's data; for ESPN and Sleeper the league is hidden and can be re-linked.
            </li>
            <li>
              <strong>Disconnect Yahoo</strong> from the dashboard. This deletes your Yahoo tokens and all
              data for your Yahoo leagues.
            </li>
            <li>
              <strong>Delete your account</strong> by asking through the contact page. We delete your
              account, credentials, and leagues within ten business days.
            </li>
          </ul>
        </Section>

        <Section title="Third parties">
          <ul className="list-disc space-y-2 pl-5">
            <li><strong>ESPN, Sleeper, Yahoo.</strong> Your fantasy platform, queried on your behalf.</li>
            <li>
              <strong>Google Analytics.</strong> Page views, including the page URL. See Google's privacy
              policy for how they handle it.
            </li>
            <li>
              <strong>Microsoft Clarity.</strong> Session replay and heatmaps on some pages so we can see
              where the site is confusing. League member lists are masked. Clarity is disabled on Yahoo
              league pages.
            </li>
            <li>
              <strong>Discord.</strong> Only if you add a webhook; recaps and cards are posted to that
              webhook.
            </li>
            <li>
              <strong>Google Gemini.</strong> Powers the optional AI Report for ESPN and Sleeper
              leagues. The report inputs are league names, owner names, scores, and player names for the
              week. Not used for Yahoo leagues.
            </li>
            <li>
              <strong>Databricks.</strong> Hosts the AI model behind Data Snacks, the weekly stat
              highlights for ESPN and Sleeper leagues. The inputs are league and owner names, scores,
              and league history. Not used for Yahoo leagues.
            </li>
            <li><strong>Resend.</strong> Sends account emails such as password resets.</li>
            <li><strong>Railway and Neon.</strong> Hosting and database providers in the United States.</li>
          </ul>
        </Section>

        <Section title="Security">
          <p>
            Passwords are hashed. Yahoo OAuth tokens are encrypted at rest. Access to production systems is
            limited to the site operator. If we learn of a breach affecting your data we will tell you, and
            notify affected platform partners, promptly.
          </p>
        </Section>

        <Section title="Attribution">
          <p>
            Fantasy data for Yahoo leagues is provided by{' '}
            <a
              href="https://football.fantasysports.yahoo.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green font-semibold hover:text-mustard"
            >
              Yahoo Fantasy
            </a>
            . Doritostats is not affiliated with ESPN, Sleeper, or Yahoo.
          </p>
        </Section>

        <Section title="Changes">
          <p>
            When this policy changes, the date at the top is updated. Material changes will be announced
            on the dashboard.
          </p>
        </Section>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
